// Admin → Professions server-state: the table query plus the write mutations.
// Every write invalidates BOTH professions caches — the admin set here and
// the project-active catalog the tags page / issue chips read — so a drift
// between the two views can't be introduced by a single forgotten call.
// Feedback policy stays with the callers: the dialog renders inline errors
// via `mutate` options; only the page-level toggle owns its toasts.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createProfession,
  deleteProfession,
  fetchAdminProfessions,
  fetchAdminTrades,
  ProfessionInUseError,
  setProfessionActive,
  updateProfessionColor,
  type IAdminProfession,
  type ICreateProfessionInput,
} from '@apis/adminProfessions';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

export const adminProfessionsKey = (siteId: string) => ['admin', 'professions', siteId] as const;

/** The project-active catalog key (issues chips / tags page population) —
 *  NOT the central analytics-flavoured `siteProfessionsKey`; see the
 *  disambiguation note in constants/queryKeys.ts. */
const siteProfessionsCatalogKey = (siteId: string) => ['site', 'professions', siteId] as const;

type ToastPush = (toast: Omit<IToast, 'id'>) => void;

export function useAdminProfessions(siteId: string | null) {
  return useQuery({
    queryKey: adminProfessionsKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchAdminProfessions(siteId!, signal),
    enabled: !!siteId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

function useProfessionsInvalidation(siteId: string): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: adminProfessionsKey(siteId) }),
      queryClient.invalidateQueries({ queryKey: siteProfessionsCatalogKey(siteId) }),
    ]);
  };
}

export function useCreateProfession(siteId: string) {
  const invalidate = useProfessionsInvalidation(siteId);
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (input: ICreateProfessionInput) => createProfession(siteId, input),
    onSuccess: invalidate,
  });
}

export function useUpdateProfessionColor(siteId: string) {
  const invalidate = useProfessionsInvalidation(siteId);
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ professionId, backgroundColor }: { professionId: string; backgroundColor: string }) =>
      updateProfessionColor(siteId, professionId, backgroundColor),
    onSuccess: invalidate,
  });
}

export function useDeleteProfession(siteId: string) {
  const invalidate = useProfessionsInvalidation(siteId);
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (professionId: string) => deleteProfession(siteId, professionId),
    onSuccess: invalidate,
  });
}

/** The table's activate/deactivate switch; owns its toast mapping (the
 *  guard reasons) since the page has no inline error surface. */
export function useToggleProfessionActive(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const invalidate = useProfessionsInvalidation(siteId ?? '');
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ profession, active }: { profession: IAdminProfession; active: boolean }) =>
      setProfessionActive(siteId!, profession._id, active),
    onSuccess: invalidate,
    onError: (err) => {
      const key =
        err instanceof ProfessionInUseError
          ? err.reason === 'tags'
            ? 'adminProfessions.inUseTags'
            : 'adminProfessions.inUseActivities'
          : 'adminProfessions.updateError';
      push({ kind: 'error', title: t(key) });
    },
  });
}

/** Workers-trade catalog for the create dialog — org-level template list, no
 *  invalidation traffic (profession writes never change it). */
const adminTradesKey = (siteId: string) => ['admin', 'trades', siteId] as const;

export function useAdminTrades(siteId: string, enabled: boolean) {
  return useQuery({
    queryKey: adminTradesKey(siteId),
    queryFn: ({ signal }) => fetchAdminTrades(siteId, signal),
    enabled,
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}
